import React, { useState } from 'react';

import dayjs from '~/timeInit';

import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

import { ExtranetPendingChange } from '~/services/ExtranetService';

import ProposedValueEditor from './ProposedValueEditor';
import { getTargetIdValue } from './changeMetadata';
import { STATUS_CHIP_COLOR } from './constants';
import {
  REQUEST_TYPE_DETAIL_LABELS,
  getExtranetApprovalLabel,
  getExtranetApprovalSectionLabel,
} from './extranetApprovalLabels';
import type { ChangeMetadata } from './types';
import ValueDiff from './valueDiff/ValueDiff';

interface ChangeDiffDialogProps {
  change: ExtranetPendingChange | null;
  metadata?: ChangeMetadata;
  isSaving: boolean;
  onClose: () => void;
  onApprove: (id: string, expectedProposedValue: Record<string, unknown>) => void;
  onEdit: (
    id: string,
    proposedValue: Record<string, unknown>,
    expectedProposedValue: Record<string, unknown>,
  ) => Promise<ExtranetPendingChange>;
  onReject: (id: string) => void;
}

const EDITABLE_REQUEST_TYPES: Record<string, true> = {
  property: true,
  property_meta: true,
  property_taxes: true,
  property_facility: true,
  property_image: true,
  room_type: true,
  room_type_image: true,
};

export default function ChangeDiffDialog(props: ChangeDiffDialogProps) {
  return <ChangeDiffDialogSession key={props.change?.id ?? 'closed'} {...props} />;
}

function ChangeDiffDialogSession({
  change,
  metadata,
  isSaving,
  onClose,
  onApprove,
  onEdit,
  onReject,
}: ChangeDiffDialogProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, unknown>>(() => change?.proposed_value ?? {});
  const [editBaseValue, setEditBaseValue] = useState<Record<string, unknown>>(
    () => change?.proposed_value ?? {},
  );
  const [savedChange, setSavedChange] = useState<ExtranetPendingChange | null>(null);
  const diffSectionLabel = change ? getExtranetApprovalSectionLabel(change.target_ids) : undefined;
  const propertyId = change ? getTargetIdValue(change.target_ids, 'property_id') : '';

  const canEditChange = change?.status === 'pending' && !!EDITABLE_REQUEST_TYPES[change.request_type];
  const isEditingCurrentChange = canEditChange && isEditing;
  const persistedProposedValue = savedChange?.proposed_value ?? change?.proposed_value ?? {};
  const proposedValue = isEditingCurrentChange ? draft : persistedProposedValue;

  const startEditing = () => {
    if (!change || !canEditChange) {
      return;
    }
    const proposedValueAtEditStart = savedChange?.proposed_value ?? change.proposed_value;
    setDraft(proposedValueAtEditStart);
    setEditBaseValue(proposedValueAtEditStart);
    setSaveError(null);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setDraft(persistedProposedValue);
    setEditBaseValue(persistedProposedValue);
    setSaveError(null);
    setIsEditing(false);
  };

  const saveEdit = async () => {
    if (!change || !isEditingCurrentChange || isSaving) {
      return;
    }
    setSaveError(null);
    try {
      const updatedChange = await onEdit(change.id, draft, editBaseValue);
      setSavedChange(updatedChange);
      setDraft(updatedChange.proposed_value);
      setEditBaseValue(updatedChange.proposed_value);
      setIsEditing(false);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Could not save this change.');
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      onClose();
    }
  };

  return (
    <Dialog open={!!change} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Change diff — {change && getExtranetApprovalLabel(change)}</DialogTitle>
      {change && (
        <Box sx={{ px: 3, pb: 1, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Partner
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {metadata?.vendorName ?? change.fk_vendor_id}
            </Typography>
            {metadata?.vendorName && (
              <Typography variant="caption" color="text.secondary">
                {change.fk_vendor_id}
              </Typography>
            )}
          </Box>
          {propertyId && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Property
              </Typography>
              <Typography variant="body2">{metadata?.propertyName ?? propertyId}</Typography>
              {metadata?.propertyName && (
                <Typography variant="caption" color="text.secondary">
                  {propertyId}
                </Typography>
              )}
            </Box>
          )}
          {(metadata?.roomName || metadata?.ratePlanName) && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Room
              </Typography>
              <Typography variant="body2">
                {[metadata.roomName, metadata.ratePlanName].filter(Boolean).join(' · ')}
              </Typography>
            </Box>
          )}
          {diffSectionLabel && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Section
              </Typography>
              <Typography variant="body2">{diffSectionLabel}</Typography>
            </Box>
          )}
          <Box>
            <Typography variant="caption" color="text.secondary">
              Change type
            </Typography>
            <Typography variant="body2">
              {REQUEST_TYPE_DETAIL_LABELS[change.request_type] ?? change.request_type}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Submitted
            </Typography>
            <Typography variant="body2">{dayjs(change.created_at).format('D/M/YY, h:mm a')}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Changed by
            </Typography>
            <Typography variant="body2">{metadata?.requestedByEmail ?? change.requested_by_user_id}</Typography>
            {metadata?.requestedByName && (
              <Typography variant="caption" color="text.secondary">
                {metadata.requestedByName}
              </Typography>
            )}
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Status
            </Typography>
            <Box>
              <Chip size="small" label={change.status} color={STATUS_CHIP_COLOR[change.status]} />
            </Box>
          </Box>
          {change.reviewed_by_user_id && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                {change.status === 'approved' ? 'Approved by' : 'Reviewed by'}
              </Typography>
              <Typography variant="body2">{metadata?.reviewedByEmail ?? change.reviewed_by_user_id}</Typography>
              {metadata?.reviewedByName && (
                <Typography variant="caption" color="text.secondary">
                  {metadata.reviewedByName}
                </Typography>
              )}
            </Box>
          )}
          {change.reviewed_at && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Reviewed at
              </Typography>
              <Typography variant="body2">{dayjs(change.reviewed_at).format('D/M/YY, h:mm a')}</Typography>
            </Box>
          )}
        </Box>
      )}
      <DialogContent dividers>
        {change && (
          <>
            {isEditingCurrentChange && (
              <Stack spacing={1.5} mb={2}>
                <ProposedValueEditor value={draft} previous={change.previous_value} onChange={setDraft} />
              </Stack>
            )}
            {saveError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {saveError}
              </Alert>
            )}
            <ValueDiff previous={change.previous_value} proposed={proposedValue} />
          </>
        )}
      </DialogContent>
      <DialogActions>
        {change?.status === 'pending' && !isEditingCurrentChange && (
          <>
            {canEditChange && (
              <Button variant="outlined" onClick={startEditing} disabled={isSaving}>
                Edit
              </Button>
            )}
            <Button variant="contained" onClick={() => onApprove(change.id, persistedProposedValue)} disabled={isSaving}>
              Approve
            </Button>
            <Button variant="text" color="error" onClick={() => onReject(change.id)} disabled={isSaving}>
              Reject
            </Button>
          </>
        )}
        {isEditingCurrentChange && (
          <>
            <Button variant="contained" onClick={saveEdit} disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Save changes'}
            </Button>
            <Button variant="text" onClick={cancelEditing} disabled={isSaving}>
              Cancel
            </Button>
          </>
        )}
        <Button variant="text" onClick={handleClose} disabled={isSaving}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
